import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FaUser, FaCrown, FaStar } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleDemoLogin = async (email, planName) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/demo-login`, { email }, {
        withCredentials: false
      });
      
      toast.success(`¡Bienvenido! Entraste como ${planName}`);
      // Usar window.location para forzar recarga completa con cookies
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);
    } catch (error) {
      toast.error('Error al iniciar sesión demo');
      console.error(error);
      setLoading(false);
    }
  };

  const demoAccounts = [
    {
      email: 'deisycp1024@gmail.com',
      name: 'Fit Evolution',
      plan: 'basic',
      icon: <FaUser className="text-4xl" />,
      color: 'from-blue-400 to-blue-600',
      description: 'Solo generadores, descuentos en contenido',
      features: ['✅ Generador de dietas', '✅ Generador de rutinas', '❌ Videos ($30 con 40% desc)', '❌ Ebooks (40% descuento)']
    },
    {
      email: 'premium@deisycfit.com',
      name: 'Plan Premium',
      plan: 'premium',
      icon: <FaStar className="text-4xl" />,
      color: 'from-purple-400 to-purple-600',
      description: 'Acceso completo a TODO',
      features: ['✅ Generador de dietas', '✅ Generador de rutinas', '✅ Videos incluidos', '✅ Ebooks incluidos']
    },
    {
      email: 'deisycfit@gmail.com',
      name: 'Plan Gold',
      plan: 'gold',
      icon: <FaCrown className="text-4xl" />,
      color: 'from-yellow-400 to-orange-600',
      description: 'Acceso VIP + contenido exclusivo',
      features: ['✅ TODO incluido', '✅ Videos', '✅ Ebooks', '✅ Contenido exclusivo Gold', '✅ Prioridad en soporte']
    },
    {
      email: 'deisycp1424@gmail.com',
      name: 'Sin Plan',
      plan: 'none',
      icon: <FaUser className="text-4xl text-gray-400" />,
      color: 'from-gray-400 to-gray-600',
      description: 'Sin acceso - TODO bloqueado',
      features: ['❌ Generadores bloqueados', '❌ Videos bloqueados ($50)', '❌ Ebooks bloqueados', '💰 Debe comprar plan básico mínimo']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 py-12 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            🔐 Login Demo - Probar Diferentes Planes
          </h1>
          <p className="text-lg text-gray-600">
            Selecciona una cuenta para ver cómo se ve cada plan
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {demoAccounts.map((account, idx) => (
            <motion.div
              key={account.email}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className="hover:shadow-2xl transition-all h-full">
                <CardHeader className={`bg-gradient-to-r ${account.color} text-white text-center`}>
                  <div className="flex justify-center mb-3">
                    {account.icon}
                  </div>
                  <CardTitle className="text-2xl">{account.name}</CardTitle>
                  <CardDescription className="text-white/90">
                    {account.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-3 mb-6">
                    {account.features.map((feature, i) => (
                      <div key={i} className="text-sm">
                        {feature}
                      </div>
                    ))}
                  </div>

                  <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-xs text-gray-400 mb-1">Email de prueba:</p>
                    <p className="text-xs font-mono break-all text-gray-300">{account.email}</p>
                  </div>

                  <Button
                    className="w-full"
                    onClick={() => handleDemoLogin(account.email, account.name)}
                    disabled={loading}
                  >
                    {loading ? 'Entrando...' : `Entrar como ${account.name}`}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 p-6 bg-blue-50 rounded-lg border-2 border-blue-200"
        >
          <h3 className="font-semibold text-blue-900 mb-2">📌 Nota para Deisy:</h3>
          <p className="text-sm text-blue-800">
            Estas son cuentas de PRUEBA para que puedas ver cómo se comporta la plataforma con diferentes planes.
            <br/>
            <strong>Fit Evolution:</strong> Verá paywalls con 40% descuento en videos y ebooks.
            <br/>
            <strong>Plan Premium:</strong> Acceso completo a videos y ebooks incluido.
            <br/>
            <strong>Plan Gold:</strong> Acceso VIP total sin restricciones + contenido exclusivo.
            <br/>
            <strong>Sin Plan:</strong> Verá precios normales sin descuentos.
          </p>
        </motion.div>

        <div className="text-center mt-8">
          <Button variant="outline" onClick={() => navigate('/')}>
            ← Volver al Inicio
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DemoLogin;
